import { Box, useTheme } from "@mui/material";
import { Tooltip } from "components";
import {
  IntegrationSourceIcon,
  RobotFaceIcon,
  SettingsGearsIcon,
  SlaTileIcon,
} from "components/icons";
import type { ServiceAuditSource } from "shared";

/**
 * Sources that get a glyph instead of a person's avatar. `manual` is absent by
 * design — a real user has a real avatar, and that's the point of the contrast.
 */
type NonManualSource = Exclude<ServiceAuditSource, "manual">;

const SOURCE_ICONS: Record<NonManualSource, JSX.Element> = {
  automation: <SettingsGearsIcon sx={{ fontSize: "14px" }} />,
  api: <IntegrationSourceIcon sx={{ fontSize: "14px" }} />,
  // Neither of these was in the mockup, which specified automation and API only.
  // Both DO arrive from the service — SLA escalations and RMM/webhook
  // transitions — and carry `activity_by.name: null`, so falling through to the
  // person avatar would render an empty circle.
  //
  // `SlaTileIcon` is the product's own SLA mark, so this screen doesn't invent
  // a second visual language for the concept — same reasoning as rendering
  // statuses through the shared `StatusBadge`.
  sla: <SlaTileIcon sx={{ fontSize: "14px" }} />,
  system: <RobotFaceIcon sx={{ fontSize: "14px" }} />,
};

const SOURCE_TOOLTIPS: Record<NonManualSource, string> = {
  automation: "Performed by an automation rule",
  api: "Performed via the API",
  sla: "Triggered by an SLA rule",
  system: "Performed by the system",
};

interface SourceAvatarProps {
  source: NonManualSource;
  /** Matches the person avatar it stands in for. */
  size: string;
}

/**
 * Stands in for the actor avatar when no person performed the action.
 *
 * The row is otherwise identical — same 24×24 circle in the same column — so
 * the timeline still reads as one list, with the glyph as the only signal that
 * a machine did this rather than a colleague.
 *
 * A tooltip carries the meaning in words: the glyphs are small, and a gear is
 * not self-evidently "automation" to someone seeing it for the first time.
 */
export const SourceAvatar = ({ source, size }: SourceAvatarProps) => {
  const theme = useTheme();

  return (
    <Tooltip title={SOURCE_TOOLTIPS[source]} placement="top">
      <Box
        // `role="img"` + the label, because the tooltip only reaches a mouse.
        // Without this the glyph is invisible to a screen reader and the row
        // loses its actor entirely.
        role="img"
        aria-label={SOURCE_TOOLTIPS[source]}
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.secondary["purple-tint-3"],
          color: theme.palette.primitive.brand.purple[700],
        }}
      >
        {SOURCE_ICONS[source]}
      </Box>
    </Tooltip>
  );
};
